/**
 * Centralized Error Logger - Production Error Tracking
 * Logs errors with context for better debugging and monitoring
 */

class ErrorLogger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.apiUrl = process.env.REACT_APP_API_URL;
  }

  /**
   * Attempt to extract user email from various sources
   * @returns {string|null} User email if found
   */
  getUserEmail() {
    try {
      // Check localStorage for user info (common pattern in React apps)
      const userInfo = localStorage.getItem('user') || localStorage.getItem('userInfo') || localStorage.getItem('currentUser');
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        if (parsed.email) return parsed.email;
      }

      // Check Auth0 token in localStorage
      const auth0Token = localStorage.getItem('authToken') || localStorage.getItem('auth0Token') || localStorage.getItem('token');
      if (auth0Token) {
        const payload = this.decodeJWT(auth0Token);
        if (payload && payload.email) return payload.email;
        if (payload && payload['https://memberportal.irishfunds.ie/email']) return payload['https://memberportal.irishfunds.ie/email'];
      }

      // Check sessionStorage as fallback
      const sessionUser = sessionStorage.getItem('user') || sessionStorage.getItem('userInfo');
      if (sessionUser) {
        const parsed = JSON.parse(sessionUser);
        if (parsed.email) return parsed.email;
      }

      return null;
    } catch (error) {
      // Silently fail - don't want error logging to cause more errors
      return null;
    }
  }

  /**
   * Decode JWT token payload (client-side only for extracting claims, not for verification)
   * @param {string} token - JWT token
   * @returns {Object|null} Decoded payload
   */
  decodeJWT(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      return JSON.parse(atob(parts[1]));
    } catch (error) {
      return null;
    }
  }

  /**
   * Log an error with context
   * @param {Error|string} error - The error to log
   * @param {Object} context - Additional context
   * @param {string} userEmail - User email if available (will auto-detect if not provided)
   */
  logError(error, context = {}, userEmail = null) {
    // Auto-detect user email if not provided
    const detectedEmail = userEmail || this.getUserEmail();
    
    const errorData = {
      timestamp: new Date().toISOString(),
      message: error?.message || error,
      stack: error?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      userEmail: detectedEmail,
      ...context
    };

    // Always log to console in development
    if (this.isDevelopment) {
      console.error('🚨 Error logged:', errorData);
    }

    // Log to backend for production monitoring
    this.sendToBackend(errorData);
  }

  /**
   * Log API errors specifically
   * @param {string} endpoint - API endpoint that failed
   * @param {Error} error - The error
   * @param {Object} requestData - Request details
   * @param {Response} response - Response object if available
   */
  logApiError(endpoint, error, requestData = {}, response = null) {
    const context = {
      type: 'API_ERROR',
      endpoint,
      method: requestData.method || 'GET',
      status: response?.status,
      statusText: response?.statusText,
      requestBody: this.sanitizeData(requestData.body),
      headers: this.sanitizeHeaders(requestData.headers)
    };

    this.logError(error, context);
  }

  /**
   * Log authentication errors
   * @param {Error} error - Auth error
   * @param {Object} context - Auth context
   */
  logAuthError(error, context = {}) {
    this.logError(error, {
      type: 'AUTH_ERROR',
      ...context
    });
  }

  /**
   * Log user activity tracking errors
   * @param {Error} error - Activity tracking error
   * @param {Object} activityData - Activity data that failed
   */
  logActivityError(error, activityData = {}) {
    this.logError(error, {
      type: 'ACTIVITY_TRACKING_ERROR',
      activityData: this.sanitizeData(activityData)
    }, activityData.userEmail);
  }

  /**
   * Log navigation/routing errors
   * @param {Error} error - Navigation error
   * @param {string} route - Route that failed
   */
  logNavigationError(error, route) {
    this.logError(error, {
      type: 'NAVIGATION_ERROR',
      route,
      previousRoute: document.referrer
    });
  }

  /**
   * Log file access errors specifically  
   * @param {string} fileId - The file ID that failed to load
   * @param {Error} error - The error
   * @param {Object} context - Additional context
   */
  logFileAccessError(fileId, error, context = {}) {
    // Auto-detect user email for file access errors
    const userEmail = this.getUserEmail();
    
    this.logError(error, {
      type: 'FILE_ACCESS_ERROR',
      fileId,
      url: window.location.href,
      referrer: document.referrer,
      ...context
    }, userEmail);
  }

  /**
   * Send error to backend for centralized logging
   * @param {Object} errorData - Error data to send
   */
  async sendToBackend(errorData) {
    try {
      // Skip sending to backend in development mode
      if (this.isDevelopment) {
        return;
      }

      // Get auth token to include in request so backend can extract user info
      let headers = {
        'Content-Type': 'application/json'
      };

      // Try to get auth token from common storage locations
      const authToken = localStorage.getItem('authToken') || 
                       localStorage.getItem('auth0Token') || 
                       localStorage.getItem('token') ||
                       sessionStorage.getItem('authToken');

      if (authToken) {
        headers['Authorization'] = authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`;
      }

      await fetch(`${this.apiUrl}logError`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(errorData)
      });
    } catch (backendError) {
      // Don't create infinite loops - just log to console if backend logging fails
      if (this.isDevelopment) {
        console.warn('Failed to send error to backend:', backendError);
      }
    }
  }

  /**
   * Remove sensitive data from logs
   * @param {Object} data - Data to sanitize
   * @returns {Object} Sanitized data
   */
  sanitizeData(data) {
    if (!data) return data;
    
    const sanitized = { ...data };
    
    // Remove sensitive fields
    const sensitiveFields = ['password', 'token', 'authorization', 'secret', 'key'];
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    return sanitized;
  }

  /**
   * Remove sensitive headers from logs
   * @param {Object} headers - Headers to sanitize
   * @returns {Object} Sanitized headers
   */
  sanitizeHeaders(headers) {
    if (!headers) return headers;
    
    const sanitized = { ...headers };
    
    if (sanitized.Authorization) {
      sanitized.Authorization = 'Bearer [REDACTED]';
    }

    return sanitized;
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

// Global error handlers for unhandled errors
if (typeof window !== 'undefined') {
  // Handle unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    errorLogger.logError(event.error || new Error(event.message), {
      type: 'UNHANDLED_ERROR',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  });

  // Handle unhandled Promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    errorLogger.logError(event.reason, {
      type: 'UNHANDLED_PROMISE_REJECTION'
    });
  });

  // Optional: Capture console.error calls (configurable)
  const CAPTURE_CONSOLE_ERRORS = process.env.REACT_APP_CAPTURE_CONSOLE_ERRORS === 'true';
  
  if (CAPTURE_CONSOLE_ERRORS) {
    const originalConsoleError = console.error;
    
    console.error = function(...args) {
      // Still show in browser console
      originalConsoleError.apply(console, args);
      
      // Convert arguments to error message
      const errorMessage = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');
      
      // Skip logging our own error logger messages to prevent loops
      if (errorMessage.includes('🚨 Error logged:') || 
          errorMessage.includes('Failed to send error to backend:')) {
        return;
      }
      
      // Skip very short or common messages that might be noise
      if (errorMessage.length < 10 || 
          errorMessage.includes('Warning:') ||
          errorMessage.includes('[HMR]') ||
          errorMessage.includes('Download the React DevTools')) {
        return;
      }
      
      // Get stack trace from current call
      const stack = new Error().stack;
      
      // Log to SharePoint with context
      errorLogger.logError(new Error(errorMessage), {
        type: 'CONSOLE_ERROR',
        source: 'console.error override',
        originalArgs: args.length,
        stack: stack
      });
    };
    
         // Console error capture enabled
   } else {
     // Console error capture disabled
   }
}

export default errorLogger; 